import { useEffect, useMemo, useState } from "react";
import {
  getOrRefreshToken,
  isTokenExpiredOrInvalid,
} from "../utilities/TokenUtil";
import {
  ActivationState,
  Client,
  IMessage,
  StompSubscription,
} from "@stomp/stompjs";
import { useNavigate } from "react-router-dom";

export interface StompClientValues {
  clientConnected: boolean;
  stompClient: Client;
  unsubscribeFromAll: () => void;
  subscribeToTopics: (subscriptionDetailsList: SubscriptionDetails[]) => void;
}

export interface SubscriptionDetails {
  destination: string;
  callback: (message: IMessage) => void;
}

const useStompClient = (
  subscriptionDetailsList: SubscriptionDetails[],
  changeConnectionStatus: (status: string) => void,
  initialSubscriptionsCreated: boolean,
): StompClientValues => {
  const [clientConnected, setClientConnected] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<StompSubscription[]>([]);
  const env = process.env.NODE_ENV;
  const brokerUrl =
    env === "development"
      ? "ws://localhost:8099/ws"
      : `wss://${window.location.host}/ws`;

  const navigate = useNavigate();

  const unsubscribeFromAll = () => {
    subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    setSubscriptions([]);
  };

  const stompClient = useMemo(() => {
    return new Client({
      brokerURL: brokerUrl,
      heartbeatOutgoing: 20000,
      heartbeatIncoming: 20000,
      reconnectDelay: 3000,
      discardWebsocketOnCommFailure: true,
    });
  }, [brokerUrl]);

  const subscribeToTopics = (
    subscriptionDetailsList: SubscriptionDetails[],
  ) => {
    subscriptionDetailsList.forEach((subscriptionDetails) => {
      stompClient.subscribe(
        subscriptionDetails.destination,
        subscriptionDetails.callback,
      );
    });
  };

  const setUpClient = async () => {
    if (env === "development") {
      stompClient.debug = (msg) => {
        console.log(msg);
      };
    }

    stompClient.beforeConnect = async () => {
      const newToken = await getOrRefreshToken().catch(() => {
        if (isTokenExpiredOrInvalid()) {
          navigate("/login");
        }
      });
      stompClient.connectHeaders = {
        Authorization: `Bearer ${newToken}`,
      };
    };

    stompClient.onConnect = () => {
      console.log("Connected");
      setClientConnected(true);
      changeConnectionStatus("Connected");
      subscriptionDetailsList.forEach((subscriptionDetails) => {
        stompClient.subscribe(
          subscriptionDetails.destination,
          subscriptionDetails.callback,
        );
      });
    };
    stompClient.onDisconnect = () => {
      console.log("Disconnected");
      setClientConnected(false);
      changeConnectionStatus("Disconnected");
    };
    stompClient.onWebSocketError = async () => {
      console.log("Websocket ERROR");
    };
    stompClient.onStompError = (frame) => {
      console.log("Broker reported error: " + frame.headers["message"]);
      console.log("Additional details: " + frame.body);
    };
    stompClient.onWebSocketClose = async (closeEvent: CloseEvent) => {
      console.log("Websocket CLOSE");
      setClientConnected(false);
      changeConnectionStatus("Reconnecting");
    };
    stompClient.onChangeState = (state: ActivationState) => {
      if (state === ActivationState.INACTIVE) {
        changeConnectionStatus("Disconnected");
      }
    };
  };

  useEffect(() => {
    console.log("stompClient useEffect firing");
    if (subscriptionDetailsList.length === 0) {
      stompClient
        .deactivate()
        .then(() => console.log("No subscriptions, disconnecting"));
      changeConnectionStatus("Disconnected");
      setClientConnected(false);
    } else if (initialSubscriptionsCreated) {
      changeConnectionStatus("Reconnecting");
      setUpClient().then(() => {
        stompClient.activate();
      });
    }

    return () => {
      stompClient.deactivate().then(() => {
        console.log("STOMP client deactivating");
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, initialSubscriptionsCreated]);

  return {
    clientConnected,
    stompClient,
    unsubscribeFromAll,
    subscribeToTopics,
  };
};

export default useStompClient;
